<template>
  <div
    class="tw-rounded-2xl tw-bg-white dark:tw-bg-slate-800 tw-border tw-border-slate-100 dark:tw-border-slate-700 tw-shadow tw-py-3.5 tw-flex tw-flex-col tw-gap-3"
  >
    <div
      class="tw-text-sm tw-text-slate-700 dark:tw-text-slate-50 tw-font-body tw-font-medium tw-px-4"
    >
      <div
        class="tw-flex tw-flex-col xl:tw-flex-row tw-gap-4 xl:tw-items-center"
      >
        <div class="tw-flex-1 tw-flex tw-flex-col tw-gap-1">
          <h3
            class="tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100 tw-font-body"
          >
            Hosting Uploads by Month
          </h3>
          <p
            v-if="rangeLabel"
            class="tw-text-xs tw-text-slate-400 dark:tw-text-slate-500 tw-font-body"
          >
            Showing
            <span
              class="tw-font-medium tw-text-slate-700 dark:tw-text-slate-200"
              >{{ rangeLabel }}</span
            >
          </p>
        </div>
        <div class="hosting-filters tw-flex tw-flex-wrap tw-gap-3 tw-items-end">
          <div class="tw-flex tw-flex-col tw-gap-1 tw-min-w-[160px]">
            <label
              class="tw-text-xs tw-uppercase tw-text-slate-500 dark:tw-text-slate-400 tw-font-semibold"
            >
              Months per page
            </label>
            <Dropdown
              v-model="selectedWindow"
              :options="windowOptions"
              optionLabel="label"
              optionValue="value"
              :panelStyle="{ maxHeight: '240px' }"
              class="hosting-select tw-border tw-border-transparent tw-outline-none tw-shadow dark:tw-bg-slate-800 dark:tw-border-slate-700 tw-w-full"
              input-class="tw-p-1 tw-pl-2 tw-text-sm tw-font-medium"
            />
          </div>
          <div
            v-if="hostOptions.length"
            class="tw-flex tw-flex-col tw-gap-1 tw-min-w-[220px]"
          >
            <label
              class="tw-text-xs tw-uppercase tw-text-slate-500 dark:tw-text-slate-400 tw-font-semibold"
            >
              Hosts
            </label>
            <MultiSelect
              v-model="selectedHosts"
              :options="hostOptions"
              optionLabel="label"
              optionValue="value"
              :panelStyle="{ maxHeight: '240px' }"
              display="chip"
              :maxSelectedLabels="3"
              placeholder="All hosts"
              class="hosting-select tw-border tw-border-transparent tw-outline-none tw-shadow dark:tw-bg-slate-800 dark:tw-border-slate-700 tw-w-full"
              :virtualScrollerOptions="{ itemSize: 36 }"
            >
              <template #chip="{ value }">
                <span class="tw-text-xs tw-font-medium">
                  {{ hostLabelMap[value]?.label || value.toUpperCase() }}
                </span>
              </template>
              <template #option="{ option }">
                <span
                  class="tw-text-sm tw-font-medium tw-text-slate-700 dark:tw-text-slate-100"
                >
                  {{ option.label }}
                </span>
              </template>
            </MultiSelect>
          </div>
          <div class="tw-flex tw-items-end tw-gap-2">
            <download-csv :data="csvData" :name="csvFileName">
              <Button
                label="CSV"
                icon="pi pi-download tw-mr-1.5 tw-text-sm"
                class="tw-h-7 tw-shadow tw-w-[68px] tw-px-2 tw-bg-white dark:tw-bg-slate-800 tw-border-transparent dark:tw-border-slate-700 tw-text-slate-700 dark:tw-text-slate-50 tw-text-sm tw-font-medium tw-font-body"
              ></Button>
            </download-csv>
            <div class="tw-flex tw-gap-2">
              <Button
                icon="pi pi-sync tw-text-slate-700 dark:tw-text-slate-50 tw-text-sm"
                class="tw-h-7 tw-shadow tw-w-8 tw-bg-white dark:tw-bg-slate-800 tw-border-transparent dark:tw-border-slate-700 tw-text-sm tw-font-medium tw-font-body"
                @click="manualRefresh"
                :disabled="loading"
              />
              <Button
                icon="pi pi-arrow-left tw-text-slate-700 dark:tw-text-slate-50 tw-text-sm"
                class="tw-h-7 tw-shadow tw-w-8 tw-bg-white dark:tw-bg-slate-800 tw-border-transparent dark:tw-border-slate-700 tw-text-sm tw-font-medium tw-font-body"
                @click="goPrevPage"
                :disabled="loading || !hasPrev"
              />
              <Button
                icon="pi pi-arrow-right tw-text-slate-700 dark:tw-text-slate-50 tw-text-sm"
                class="tw-h-7 tw-shadow tw-w-8 tw-bg-white dark:tw-bg-slate-800 tw-border-transparent dark:tw-border-slate-700 tw-text-sm tw-font-medium tw-font-body"
                @click="goNextPage"
                :disabled="loading || !hasMore"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="errorMessage" class="tw-px-4">
      <div
        class="tw-text-sm tw-text-red-600 tw-bg-red-50 dark:tw-bg-red-900/30 tw-border tw-border-red-200 dark:tw-border-red-900 tw-rounded-md tw-px-3 tw-py-2"
      >
        {{ errorMessage }}
      </div>
    </div>

    <div
      class="tw-px-4 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400 tw-flex tw-justify-between tw-items-center"
    >
      <span v-if="!rangeLabel" class="tw-font-medium tw-text-slate-400">
        No data yet
      </span>
      <span class="tw-text-right"
        >Page {{ page
        }}<span v-if="totalPages > 1"> / {{ totalPages }}</span></span
      >
    </div>

    <div class="tw-p-4 tw-pt-2 tw-overflow-x-auto tw-w-full">
      <DataTable
        :value="tableRows"
        dataKey="key"
        :loading="loading"
        responsiveLayout="scroll"
        class="hosting-monthly-table tw-bg-transparent"
        table-class="tw-bg-transparent"
        :rowClass="() => 'tw-bg-transparent'"
        :showGridlines="false"
        :paginator="false"
        tableStyle="width: 100%; min-width: 720px;"
        :emptyMessage="loading ? '' : 'No records for the selected period.'"
      >
        <Column
          field="label"
          headerClass="hosting-header tw-text-left"
          bodyClass="hosting-cell tw-text-left"
          footerClass="hosting-footer tw-text-left"
          style="min-width: 160px;"
        >
          <template #header>MONTH</template>
          <template #body="{ data }">{{ data.label }}</template>
          <template #footer>Totals</template>
        </Column>
        <Column
          v-for="column in visibleColumns"
          :key="column.key"
          :field="column.field"
          :header="column.header"
          :headerTooltip="column.tooltip"
          headerClass="hosting-header tw-text-right"
          bodyClass="hosting-cell tw-text-right"
          footerClass="hosting-footer tw-text-right"
          :style="{ minWidth: '120px' }"
        >
          <template #body="{ data }">
            {{ formatNumber(data[column.field]) }}
          </template>
          <template #footer>
            {{ formatNumber(summaryRow[column.field]) }}
          </template>
        </Column>
      </DataTable>
    </div>

    <div
      class="tw-flex tw-justify-between tw-items-center tw-px-4 tw-pb-3 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400"
    >
      <span>
        Total active shows on Jamx:
        <span class="tw-font-medium tw-text-slate-700 dark:tw-text-slate-200">
          {{ formatNumber(totalActiveShows) }}
        </span>
      </span>
    </div>
  </div>
</template>

<script setup>
/* global defineProps */
import { computed, onMounted, ref, watch } from "vue";
import axios from "axios";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Dropdown from "primevue/dropdown";
import MultiSelect from "primevue/multiselect";
import Button from "primevue/button";
import { API_BASE_URL } from "@/consts";

const props = defineProps({
  token: {
    type: String,
    required: true,
  },
});

const monthLabelFormatter = new Intl.DateTimeFormat("en-US", {
  month: "long",
  year: "numeric",
});

const windowOptions = [
  { label: "3 months", value: 3 },
  { label: "6 months", value: 6 },
  { label: "12 months", value: 12 },
];

const selectedWindow = ref(12);
const loading = ref(false);
const errorMessage = ref("");
const monthsData = ref([]);
const hostMetadata = ref([]);
const selectedHosts = ref([]);
const totalActiveShows = ref(0);
const page = ref(1);
const rangeInfo = ref(null);
let activeRequestId = 0;
let hostsInitialized = false;

const allHosts = computed(() => hostMetadata.value.map((meta) => meta.host));

const csvData = computed(() => {
  const hosts = allHosts.value;
  return (monthsData.value || []).map((month) => {
    const monthTotals = month?.totals || zeroTotals();
    const row = {
      month: month.label || formatMonth(month.month),
      total_all: monthTotals.totalShows || 0,
      total_non_ai: monthTotals.nonAiShows || 0,
      total_ai: monthTotals.aiShows || 0,
    };

    hosts.forEach((host) => {
      const metrics = month?.hosts?.[host] || zeroTotals();
      row[`${host}_total`] = metrics.totalShows || 0;
      row[`${host}_non_ai`] = metrics.nonAiShows || 0;
      row[`${host}_ai`] = metrics.aiShows || 0;
    });

    return row;
  });
});

const csvFileName = computed(() => {
  if (rangeInfo.value?.startMonth && rangeInfo.value?.endMonth) {
    return `hosting-monthly-${rangeInfo.value.startMonth}-${rangeInfo.value.endMonth}.csv`;
  }
  return "hosting-monthly.csv";
});

const hostOptions = computed(() =>
  hostMetadata.value.map((meta) => ({
    label: meta.label || meta.host,
    value: meta.host,
    fullLabel: meta.fullLabel || meta.label || meta.host,
  }))
);

const hostLabelMap = computed(() => {
  const map = {};
  hostMetadata.value.forEach((meta) => {
    map[meta.host] = {
      label: meta.label || meta.host,
      fullLabel: meta.fullLabel || meta.label || meta.host,
    };
  });
  return map;
});

const sortedMonths = computed(() => {
  const items = Array.isArray(monthsData.value) ? [...monthsData.value] : [];
  return items.sort((a, b) => new Date(b.month) - new Date(a.month));
});

const totalPages = computed(() => {
  const size = Number(selectedWindow.value) || 1;
  const total = sortedMonths.value.length;
  return total ? Math.max(1, Math.ceil(total / size)) : 1;
});

const hasMore = computed(() => page.value < totalPages.value);
const hasPrev = computed(() => page.value > 1);

watch(totalPages, (newTotal) => {
  if (page.value > newTotal) {
    page.value = newTotal;
  }
});

const visibleMonths = computed(() => {
  const size = Number(selectedWindow.value) || 1;
  const startIndex = (page.value - 1) * size;
  return sortedMonths.value.slice(startIndex, startIndex + size);
});

const tableRows = computed(() => {
  return visibleMonths.value.map((month) => {
    const monthTotals = month.totals || zeroTotals();
    const row = {
      key: month.month,
      label: month.label || formatMonth(month.month),
      total_all: monthTotals.totalShows || 0,
      total_non_ai: monthTotals.nonAiShows || 0,
      total_ai: monthTotals.aiShows || 0,
    };

    selectedHosts.value.forEach((host) => {
      const metrics = month.hosts?.[host] || zeroTotals();
      row[`host_${host}_total`] = metrics.totalShows || 0;
      row[`host_${host}_non_ai`] = metrics.nonAiShows || 0;
      row[`host_${host}_ai`] = metrics.aiShows || 0;
    });

    return row;
  });
});

const summaryRow = computed(() => {
  const totals = {
    total_all: 0,
    total_non_ai: 0,
    total_ai: 0,
  };

  selectedHosts.value.forEach((host) => {
    totals[`host_${host}_total`] = 0;
    totals[`host_${host}_non_ai`] = 0;
    totals[`host_${host}_ai`] = 0;
  });

  visibleMonths.value.forEach((month) => {
    const monthTotals = month.totals || zeroTotals();
    totals.total_all += monthTotals.totalShows || 0;
    totals.total_non_ai += monthTotals.nonAiShows || 0;
    totals.total_ai += monthTotals.aiShows || 0;

    selectedHosts.value.forEach((host) => {
      const metrics = month.hosts?.[host] || zeroTotals();
      totals[`host_${host}_total`] += metrics.totalShows || 0;
      totals[`host_${host}_non_ai`] += metrics.nonAiShows || 0;
      totals[`host_${host}_ai`] += metrics.aiShows || 0;
    });
  });

  return totals;
});

const visibleColumns = computed(() => {
  const columns = [
    {
      key: "total_all",
      field: "total_all",
      header: "ALL",
      tooltip: "Total shows across all hosts",
    },
    {
      key: "total_non_ai",
      field: "total_non_ai",
      header: "ALL NAI",
      tooltip: "Non-AI shows across all hosts",
    },
    {
      key: "total_ai",
      field: "total_ai",
      header: "ALL AI",
      tooltip: "AI shows across all hosts",
    },
  ];

  selectedHosts.value.forEach((host) => {
    const meta = hostLabelMap.value[host];
    const base = meta?.label || host.toUpperCase();
    const full = meta?.fullLabel || base;

    columns.push(
      {
        key: `${host}-total`,
        field: `host_${host}_total`,
        header: `${base} ALL`,
        tooltip: `${full} total shows`,
      },
      {
        key: `${host}-non-ai`,
        field: `host_${host}_non_ai`,
        header: `${base} NAI`,
        tooltip: `${full} non-AI shows`,
      },
      {
        key: `${host}-ai`,
        field: `host_${host}_ai`,
        header: `${base} AI`,
        tooltip: `${full} AI shows`,
      }
    );
  });

  return columns;
});

const rangeLabel = computed(() => {
  if (rangeInfo.value?.startMonth && rangeInfo.value?.endMonth) {
    const start = formatMonth(rangeInfo.value.startMonth);
    const end = formatMonth(rangeInfo.value.endMonth);
    return start === end ? start : `${start} - ${end}`;
  }

  if (!sortedMonths.value.length) {
    return "";
  }

  const newest = sortedMonths.value[0];
  const oldest = sortedMonths.value[sortedMonths.value.length - 1];
  const start = newest ? formatMonth(newest.month) : "";
  const end = oldest ? formatMonth(oldest.month) : "";
  return start === end ? start : `${end} - ${start}`;
});

function formatMonth(key) {
  if (!key) {
    return "";
  }
  const date = new Date(key);
  if (Number.isNaN(date.getTime())) {
    return key;
  }
  return monthLabelFormatter.format(date);
}

function zeroTotals() {
  return { totalShows: 0, aiShows: 0, nonAiShows: 0 };
}

function formatNumber(value) {
  const number = Number(value);
  if (!Number.isFinite(number) || number === 0) {
    return "0";
  }
  return new Intl.NumberFormat("en-US").format(number);
}

const fetchData = async () => {
  if (loading.value) {
    return;
  }

  const previousHosts = [...selectedHosts.value];
  const requestId = ++activeRequestId;
  loading.value = true;
  errorMessage.value = "";

  try {
    const params = {
      months: selectedWindow.value,
    };

    const { data } = await axios.get(
      `${API_BASE_URL}/podcast-admin/analytics/hosting/monthly`,
      {
        params,
        headers: {
          Authorization: `Bearer ${props.token}`,
        },
      }
    );

    if (requestId !== activeRequestId) {
      return;
    }

    const payload = data?.data;

    if (!payload) {
      monthsData.value = [];
      hostMetadata.value = [];
      selectedHosts.value = [];
      totalActiveShows.value = 0;
      rangeInfo.value = null;
      page.value = 1;
      return;
    }

    const metadata = Array.isArray(payload.hostMetadata)
      ? payload.hostMetadata
      : [];
    hostMetadata.value = metadata;
    const hostList = metadata.map((meta) => meta.host);

    monthsData.value = Array.isArray(payload.months) ? payload.months : [];
    totalActiveShows.value = payload.totalActiveShows ?? 0;
    rangeInfo.value = payload.range || null;

    if (hostList.length) {
      const preserved = previousHosts.filter((host) => hostList.includes(host));

      if (!hostsInitialized) {
        selectedHosts.value = preserved.length ? preserved : [...hostList];
        hostsInitialized = true;
      } else if (previousHosts.length === 0) {
        selectedHosts.value = [];
      } else if (preserved.length) {
        selectedHosts.value = preserved;
      } else {
        selectedHosts.value = [...hostList];
      }
    } else {
      selectedHosts.value = [];
    }

    page.value = 1;
  } catch (error) {
    if (requestId !== activeRequestId) {
      return;
    }

    console.error("Failed to load hosting analytics", error);
    errorMessage.value =
      error?.response?.data?.error || "Unable to load hosting analytics.";
    monthsData.value = [];
    hostMetadata.value = [];
    selectedHosts.value = [];
    totalActiveShows.value = 0;
    rangeInfo.value = null;
    page.value = 1;
  } finally {
    if (requestId === activeRequestId) {
      loading.value = false;
    }
  }
};

const manualRefresh = () => {
  fetchData();
};

const goPrevPage = () => {
  if (loading.value || page.value <= 1) {
    return;
  }

  page.value = Math.max(1, page.value - 1);
};

const goNextPage = () => {
  if (loading.value || page.value >= totalPages.value) {
    return;
  }

  page.value = Math.min(totalPages.value, page.value + 1);
};

watch(selectedWindow, (newValue, oldValue) => {
  if (newValue === oldValue) {
    return;
  }
  page.value = 1;
});

watch(selectedHosts, (current) => {
  if (!hostMetadata.value.length) {
    return;
  }

  const availableHosts = new Set(hostMetadata.value.map((meta) => meta.host));
  const validSelection = current.filter((host) => availableHosts.has(host));

  if (validSelection.length !== current.length) {
    selectedHosts.value = validSelection;
  }
});

onMounted(() => {
  fetchData();
});
</script>

<style>
.hosting-select .p-dropdown-label,
.hosting-select .p-multiselect-label {
  min-height: 2.25rem;
  padding: 0.375rem 0.75rem;
  display: flex;
  align-items: center;
}

.hosting-monthly-table .p-paginator,
.hosting-monthly-table .p-paginator-bottom {
  background-color: transparent !important;
  border-color: transparent !important;
}

.hosting-header,
.hosting-cell,
.hosting-footer {
  white-space: nowrap;
}

.hosting-monthly-table .p-datatable-thead th {
  background-color: transparent !important;
}

.hosting-monthly-table td,
.hosting-monthly-table th {
  border-color: #f1f5f9 !important;
}

.tw-dark .hosting-monthly-table td,
.tw-dark .hosting-monthly-table th {
  border-color: #334155 !important;
}
</style>


